import type { ReactNode } from "react";
import { format } from "date-fns";
import { usePlotArea, useXAxisScale, ZIndexLayer } from "recharts";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { cn } from "utils";

const BADGE_SIZE = 40;

export const DateMarker = ({
  xValue,
  date,
  title,
  label,
  description,
  icon,
  className,
  onClick,
  ariaLabel,
}: {
  xValue: string;
  date: Date;
  title: string;
  label: string;
  description: ReactNode;
  icon: ReactNode;
  className?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) => {
  const xScale = useXAxisScale();
  const plotArea = usePlotArea();

  const x = xScale?.(xValue, { position: "middle" });
  if (x == null || plotArea == null) {
    return null;
  }

  const dateLabel = format(date, "d MMM yyyy");
  const badgeCy = plotArea.y / 2;
  const badgeBottom = badgeCy + BADGE_SIZE - 15;

  return (
    <>
      <ZIndexLayer zIndex={100}>
        <line
          x1={x}
          x2={x}
          y1={badgeBottom}
          y2={plotArea.y + plotArea.height}
          stroke="var(--color-secondary-300)"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />
      </ZIndexLayer>
      <ZIndexLayer zIndex={600}>
        <foreignObject
          x={x - BADGE_SIZE / 2}
          y={badgeCy - BADGE_SIZE / 2}
          width={BADGE_SIZE}
          height={BADGE_SIZE}
          style={{ overflow: "visible" }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={ariaLabel ?? `${title}: ${dateLabel}`}
                onClick={onClick}
                className={cn(
                  "absolute left-1/2 top-0 flex h-10 min-w-10 -translate-x-1/2 flex-col items-center justify-center whitespace-nowrap rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  className,
                )}
              >
                {icon}
                <div className="mt-1 text-2xs">{label}</div>
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="shadow-tooltip text-secondary-600 border-none max-w-64"
              sideOffset={6}
            >
              <TooltipArrow
                className="fill-white drop-shadow-xs"
                width={12}
                height={6}
              />
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold">{title}</span>
                <span className="text-muted-foreground">{dateLabel}</span>
              </div>
              <div className="mt-1.5 leading-relaxed text-muted-foreground">
                {description}
              </div>
            </TooltipContent>
          </Tooltip>
        </foreignObject>
      </ZIndexLayer>
    </>
  );
};
